import { SensorgroupFromJSON, SensorGroupsApi } from "@inductosense/typescript-fetch";
import AuthenticationService from "Services/Platform/AuthenticationService";
import InductosenseService from "Services/Platform/InductosenseService";
import merge from "Utilities/Merge";
import SensorGroup from "Model/SensorGroup";
import wrapWithErrorHandling from "Services/Platform/WrapWithErrorHandling";
import Sensor from "../../Model/Sensor";

interface AreaRootHeading {
    sensorGroupId: string;
    areaName: string;
}

interface SensorGroupsWithAreasResponse {
    rootSensorGroup: unknown;
    areaRoots: AreaRootHeading[];
}

// This metadata is supplied only by the opt-in Area-aware endpoint. It preserves
// the real SensorGroup root ID for iDART1 create workflows, while allowing the
// tree to display that group as an Area heading when appropriate.
export type AreaAwareSensorGroup = SensorGroup & {
    idartAreaRootNames?: Record<string, string>;
};

// The Areas endpoint is newer than the generated client, so it has to be
// requested manually. Keep using the generated group's converter after the
// raw response is read: it recursively restores every model type in the tree,
// including the Material, SensorType and warning parameter dates that the
// Configure Sensor form serialises when selecting its default values.
export const deserializeSensorGroup = (rawSensorGroup: unknown): AreaAwareSensorGroup =>
    SensorgroupFromJSON(rawSensorGroup) as AreaAwareSensorGroup;

const isSensorGroupsWithAreasResponse = (response: unknown): response is SensorGroupsWithAreasResponse =>
    typeof response === "object"
    && response !== null
    && "rootSensorGroup" in response
    && "areaRoots" in response
    && Array.isArray((response as SensorGroupsWithAreasResponse).areaRoots);

// The generated API client represents the long-standing one-root endpoint only.
// Keep that contract unchanged for installed iDART1 clients and add the opt-in
// request here until the generated client includes the new endpoint.
class SensorGroupsWithAreasApi extends SensorGroupsApi {
    async sensorGroupsWithAreasGet(): Promise<unknown> {
        const headers: Record<string, string> = {};
        const token = this.configuration.accessToken;
        const tokenString = token ? token("JWTBearer", []) : undefined;

        if (tokenString) {
            headers.Authorization = `Bearer ${tokenString}`;
        }

        const response = await this.request({
            path: "/sensor-groups/with-areas",
            method: "GET",
            headers
        });

        return response.json();
    }
}

export default class SensorGroupsService extends InductosenseService<SensorGroupsWithAreasApi> {
    constructor(protected authenticationService: AuthenticationService) {
        super(SensorGroupsWithAreasApi, authenticationService);
    }

    private async ensureLatestConfig() {
        await this.ensureAccessConfiguration();
    }

    @wrapWithErrorHandling()
    async getRootSensorGroup() {
        await this.ensureLatestConfig();
        let result: AreaAwareSensorGroup;

        try {
            // Updated iDART1 explicitly opts into the Area-aware response. The
            // old endpoint stays unchanged for already-installed clients.
            const response = await this.serverApi!.sensorGroupsWithAreasGet();

            if (isSensorGroupsWithAreasResponse(response)) {
                result = deserializeSensorGroup(response.rootSensorGroup);
                result.idartAreaRootNames = Object.fromEntries(
                    response.areaRoots.map(areaRoot => [areaRoot.sensorGroupId, areaRoot.areaName])
                );
            } else {
                // The endpoint was introduced during this feature. Accept its
                // earlier tree-only shape as well, so API and iDART1 deployments
                // do not need to occur in a particular order.
                result = deserializeSensorGroup(response);
            }
        } catch (error) {
            // This lets the updated client still connect to an older/on-prem API.
            // It will show the old one-root tree in that case, rather than failing.
            if (!(error instanceof Response) || error.status !== 404) throw error;
            result = await this.serverApi!.sensorGroupsGet() as AreaAwareSensorGroup;
        }

        if (!result) throw Error("Root sensor group was null or undefined.");
        return result;
    }

    @wrapWithErrorHandling()
    async getSensorList() {
        await this.ensureLatestConfig();
        const rootSensorGroup = await this.getRootSensorGroup();
        const addSensorGroup = (sensorGroup: SensorGroup): Sensor[] => {
            const childSensorArrays = sensorGroup.children?.map(csg => addSensorGroup(csg)) || [];
            const childSensors = ([] as Sensor[]).concat.apply([], childSensorArrays);

            return [...(sensorGroup.sensors || []), ...childSensors];
        };

        return addSensorGroup(rootSensorGroup);
    }

    @wrapWithErrorHandling()
    async addSensorGroup(name: string, parentGroupId: string, rfidIfMES?: string) {
        await this.ensureLatestConfig();

        const apiResponse = await this.serverApi!.sensorGroupsPostRaw({ sensorgrouppost: { parentGroupId, name, rfidIfMES } });
        const location = apiResponse.raw.headers.get("Location");
        if (location === null) {
            throw new Error("No location in API response");
        }

        const rxp = new RegExp("/v1/sensor-groups/(.*)").exec(location);
        const readingId = rxp![1];
        return readingId;
    }

    @wrapWithErrorHandling()
    async deleteSensorGroup(id: string) {
        await this.ensureLatestConfig();
        await this.serverApi.sensorGroupsIdDelete({ id });
    }

    @wrapWithErrorHandling()
    async updateSensorGroup(sensorGroup: SensorGroup, changes: Partial<SensorGroup>) {
        await this.ensureLatestConfig();
        if (!sensorGroup.id) throw new Error("Sensor group ID should never be undefined at this point.");

        await this.serverApi!.sensorGroupsIdPut({
            id: sensorGroup.id,
            sensorgrouppost: merge(sensorGroup, changes)
        });
    }

    @wrapWithErrorHandling()
    async moveSensorGroup(sensorGroup: SensorGroup, destinationId: string) {
        await this.ensureLatestConfig();
        const { id, ...otherProps } = sensorGroup;
        if (!id) throw new Error("Source sensor ID should never be undefined at this point.");

        if (sensorGroup.parentGroupId !== destinationId) {
            await this.serverApi!.sensorGroupsIdPut({
                id, sensorgrouppost: {
                    ...otherProps,
                    parentGroupId: destinationId
                }
            });
        }
    }
}
